import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Wand2, Loader2, Mic, User2, Coins, Volume2, Crown, Pause } from 'lucide-react';
import toast from 'react-hot-toast';
import { videoStudioService } from '../../services/videoStudioService';
import { ASPECT_RATIOS, GeneratedVideo, HeygenAvatar, HeygenVoice, VideoCreditBalance } from '../../types/videoStudio';
import AvatarStudio from './AvatarStudio';
import AIPromptHelper from './AIPromptHelper';

interface Props {
  onGenerated: (video: GeneratedVideo) => void;
  balance: VideoCreditBalance | null;
  onBalanceRefresh: () => void;
  onBuyCredits: () => void;
  configured: boolean;
}

const HeygenGenerator: React.FC<Props> = ({ onGenerated, balance, onBalanceRefresh, onBuyCredits, configured }) => {
  const { t } = useTranslation();
  const [script, setScript] = useState('');
  const [avatars, setAvatars] = useState<HeygenAvatar[]>([]);
  // Persist avatar/voice/ratio choices across sessions — easy to forget switching back.
  const [avatarId, setAvatarIdState] = useState<string>(() => {
    try { return localStorage.getItem('hg_avatar_id') || ''; } catch { return ''; }
  });
  const [voiceId, setVoiceIdState] = useState<string>(() => {
    try { return localStorage.getItem('hg_voice_id') || ''; } catch { return ''; }
  });
  const [aspectRatio, setAspectRatioState] = useState<string>(() => {
    try { return localStorage.getItem('hg_aspect_ratio') || '16:9'; } catch { return '16:9'; }
  });
  const setAvatarId = (id: string) => {
    setAvatarIdState(id);
    try { localStorage.setItem('hg_avatar_id', id); } catch {}
  };
  const setVoiceId = (id: string) => {
    setVoiceIdState(id);
    try { localStorage.setItem('hg_voice_id', id); } catch {}
  };
  const setAspectRatio = (r: string) => {
    setAspectRatioState(r);
    try { localStorage.setItem('hg_aspect_ratio', r); } catch {}
  };
  const [background, setBackground] = useState('#0F172A');
  const [caption, setCaption] = useState<boolean>(() => {
    try { return localStorage.getItem('hg_caption') === '1'; } catch { return false; }
  });
  const setCaptionPersisted = (v: boolean) => {
    setCaption(v);
    try { localStorage.setItem('hg_caption', v ? '1' : '0'); } catch {}
  };
  const [estimatedCost, setEstimatedCost] = useState<number | null>(null);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');

  useEffect(() => {
    if (!configured) return;
    videoStudioService.listAvatars().then(r => {
      if (r.success && r.data) {
        setAvatars(r.data);
        const stillExists = avatarId && r.data.some(a => a.id === avatarId);
        if (!stillExists && r.data.length) setAvatarId(r.data[0].id);
      }
    });
    // Voices are loaded lazily inside <VoiceLanguagePicker>: the language list
    // first, then voices for whatever language the user picks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configured]);

  useEffect(() => {
    // Heygen cost is per 30s segment — estimate assuming spoken text duration (~3 words/sec for natural pace)
    const words = script.trim().split(/\s+/).filter(Boolean).length;
    const seconds = Math.max(5, Math.ceil(words / 2.5));
    const isPremium = avatars.find(a => a.id === avatarId)?.premium;
    videoStudioService.estimate({
      provider: 'heygen',
      parameters: { duration: seconds, avatar_tier: isPremium ? 'premium' : 'standard' }
    }).then(r => { if (r.success && r.data) setEstimatedCost(r.data.cost); }).catch(() => {});
  }, [script, avatarId, avatars]);

  const insufficient = balance && estimatedCost !== null && balance.available < estimatedCost;


  const handleGenerate = async () => {
    if (!script.trim()) {
      toast.error(t('videoStudio.heygen.errScript') as string);
      return;
    }
    if (!avatarId || !voiceId) {
      toast.error(t('videoStudio.heygen.errPick') as string);
      return;
    }
    if (insufficient) {
      onBuyCredits();
      return;
    }

    setGenerating(true);
    setProgress(0);
    setProgressLabel(t('videoStudio.heygen.progressUploading') as string);
    try {
      const res = await videoStudioService.generate(
        {
          provider: 'heygen',
          prompt: script,
          aspect_ratio: aspectRatio as any,
          parameters: {
            avatar_id: avatarId,
            voice_id: voiceId,
            background,
            caption,
            avatar_tier: avatars.find(a => a.id === avatarId)?.premium ? 'premium' : 'standard'
          }
        },
        (pct) => {
          setProgress(pct);
          if (pct < 20) setProgressLabel(t('videoStudio.heygen.progressVoice') as string);
          else if (pct < 60) setProgressLabel(t('videoStudio.heygen.progressRecording') as string);
          else if (pct < 90) setProgressLabel(t('videoStudio.heygen.progressLipSync') as string);
          else setProgressLabel(t('videoStudio.heygen.progressSaving') as string);
        }
      );
      if (res.success && res.data) {
        onGenerated(res.data);
        toast.success(t('videoStudio.heygen.success') as string);
        onBalanceRefresh();
      }
    } catch (e: any) {
      const msg = e.response?.data?.message || e.message || (t('videoStudio.heygen.errGeneration') as string);
      if (e.response?.status === 402) {
        toast.error(t('videoStudio.heygen.insufficientCredits') as string);
        onBuyCredits();
      } else {
        toast.error(msg);
      }
    } finally {
      setGenerating(false);
      setProgress(0);
    }
  };

  if (!configured) {
    return (
      <div className="p-8 text-center rounded-xl bg-white dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800">
        <Crown className="w-10 h-10 text-amber-400 mx-auto mb-3" />
        <p className="text-gray-900 dark:text-white font-semibold mb-1">{t('videoStudio.heygen.notAvailable')}</p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('videoStudio.heygen.notAvailableHint')}
        </p>
      </div>
    );
  }

  const wordsCount = script.trim().split(/\s+/).filter(Boolean).length;
  const secondsEst = Math.ceil(wordsCount / 2.5);
  return (
    <div className="space-y-5">
      <div>
        <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-2">
          <Mic className="w-3.5 h-3.5" />
          {t('videoStudio.heygen.scriptLabel')}
        </label>
        {/* Brand-aware AI helper ABOVE the textarea — user describes intent, AI writes the script */}
        <div className="mb-2">
          <AIPromptHelper engine="heygen" currentText={script} onApply={setScript} />
        </div>
        <textarea
          value={script}
          onChange={(e) => setScript(e.target.value)}
          placeholder={t('videoStudio.heygen.scriptPlaceholder') as string}
          rows={5}
          className="w-full px-4 py-3 rounded-xl bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:border-primary-500/50 resize-none"
        />
        <div className="text-[11px] text-gray-500 mt-1.5 flex items-center justify-between">
          <span>{t('videoStudio.heygen.wordsHint', { words: wordsCount, seconds: secondsEst })}</span>
          <span className="text-gray-600">{t('videoStudio.heygen.wordsTip')}</span>
        </div>
      </div>

      <AvatarStudio
        selectedId={avatarId || null}
        onAvatarReady={(a) => { setAvatars(prev => prev.find(x => x.id === a.id) ? prev : [a, ...prev]); setAvatarId(a.id); }}
      />
      {avatarId && (
        <div className="text-[11px] text-primary-300 px-2 py-1 rounded bg-primary-500/10 border border-primary-500/20 inline-block">
          {t('videoStudio.heygen.avatarSelected', { name: avatars.find(a => a.id === avatarId)?.name || avatarId })}
        </div>
      )}

      <div>
        <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-2">
          <Volume2 className="w-3.5 h-3.5" />
          {t('videoStudio.heygen.voice')}
        </label>
        <VoiceLanguagePicker
          selectedVoiceId={voiceId}
          onSelectVoice={setVoiceId}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{t('videoStudio.heygen.aspectRatio')}</label>
          <select
            value={aspectRatio}
            onChange={(e) => setAspectRatio(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary-500/50"
          >
            {ASPECT_RATIOS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{t('videoStudio.heygen.background')}</label>
          <input
            type="color"
            value={background.startsWith('#') ? background : '#0F172A'}
            onChange={(e) => setBackground(e.target.value)}
            className="w-full h-9 rounded-lg bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 cursor-pointer"
          />
        </div>
      </div>

      {/* Burned-in captions toggle — HeyGen renders subtitles directly into the video */}
      <button
        type="button"
        onClick={() => setCaptionPersisted(!caption)}
        className={`w-full inline-flex items-center justify-between gap-3 px-4 py-3 rounded-xl border transition ${
          caption
            ? 'bg-fuchsia-500/15 border-fuchsia-500/50 text-gray-900 dark:text-white'
            : 'bg-white dark:bg-gray-900/40 border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5'
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-9 h-5 rounded-full relative transition ${caption ? 'bg-fuchsia-500' : 'bg-gray-300 dark:bg-gray-700'}`}>
            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${caption ? 'left-4' : 'left-0.5'}`} />
          </span>
          <span className="text-sm font-semibold">{t('videoStudio.heygen.captionToggle')}</span>
        </div>
        <span className="text-[10px] text-gray-500 text-right">
          {caption ? t('videoStudio.heygen.captionOn') : t('videoStudio.heygen.captionOff')}
        </span>
      </button>

      <div className="rounded-xl bg-gradient-to-br from-gray-900/80 to-gray-950 border border-gray-200 dark:border-gray-800 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-gray-300 text-xs uppercase tracking-wider font-semibold">
            <Coins className="w-3.5 h-3.5" />
            {t('videoStudio.heygen.estimatedCost')}
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-bold text-white">{estimatedCost ?? '—'}</span>
            <span className="text-xs text-gray-400">{t('videoStudio.heygen.credits')}</span>
          </div>
        </div>

        {progress > 0 && generating && (
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-600 dark:text-gray-400">{progressLabel}</span>
              <span className="text-xs text-gray-700 dark:text-gray-300 tabular-nums">{progress}%</span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-gray-800 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-primary-500 to-purple-500 transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        <button
          onClick={handleGenerate}
          disabled={generating || !script.trim()}
          className="w-full py-3 rounded-xl bg-gradient-to-r from-primary-500 to-purple-600 text-white font-semibold flex items-center justify-center gap-2 hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {generating ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> {t('videoStudio.heygen.generating')}</>
          ) : (
            <><Wand2 className="w-4 h-4" /> {t('videoStudio.heygen.cta')}</>
          )}
        </button>
        {insufficient && !generating && (
          <p className="text-xs text-rose-400 mt-2 text-center">
            {t('videoStudio.heygen.insufficientHint')}
            <button onClick={onBuyCredits} className="underline ml-1">{t('videoStudio.heygen.buyNow')}</button>
          </p>
        )}
      </div>
    </div>
  );
};

// Maps the HeyGen `language` field (a free-text English name) to a flag emoji
// + a short label. Anything we don't recognize ends up under "Outros".
const LANG_META: Record<string, { flag: string; label: string }> = {
  portuguese: { flag: '🇧🇷', label: 'Português' },
  english:    { flag: '🇺🇸', label: 'English' },
  spanish:    { flag: '🇪🇸', label: 'Español' },
  french:     { flag: '🇫🇷', label: 'Français' },
  german:     { flag: '🇩🇪', label: 'Deutsch' },
  italian:    { flag: '🇮🇹', label: 'Italiano' },
  polish:     { flag: '🇵🇱', label: 'Polski' },
  danish:     { flag: '🇩🇰', label: 'Dansk' },
  dutch:      { flag: '🇳🇱', label: 'Nederlands' },
  chinese:    { flag: '🇨🇳', label: '中文' },
  japanese:   { flag: '🇯🇵', label: '日本語' },
  korean:     { flag: '🇰🇷', label: '한국어' },
  filipino:   { flag: '🇵🇭', label: 'Filipino' },
  tagalog:    { flag: '🇵🇭', label: 'Tagalog' },
  russian:    { flag: '🇷🇺', label: 'Русский' },
  arabic:     { flag: '🇸🇦', label: 'العربية' },
  hindi:      { flag: '🇮🇳', label: 'हिन्दी' },
  turkish:    { flag: '🇹🇷', label: 'Türkçe' },
  swedish:    { flag: '🇸🇪', label: 'Svenska' },
  norwegian:  { flag: '🇳🇴', label: 'Norsk' },
  finnish:    { flag: '🇫🇮', label: 'Suomi' },
  indonesian: { flag: '🇮🇩', label: 'Indonesia' },
  thai:       { flag: '🇹🇭', label: 'ไทย' },
  vietnamese: { flag: '🇻🇳', label: 'Tiếng Việt' },
  ukrainian:  { flag: '🇺🇦', label: 'Українська' },
  greek:      { flag: '🇬🇷', label: 'Ελληνικά' },
  hebrew:     { flag: '🇮🇱', label: 'עברית' },
  czech:      { flag: '🇨🇿', label: 'Čeština' }
};

// Two-phase loader:
//   Phase 1 — fetch the language list with counts (small payload, ~30 entries)
//   Phase 2 — when the user picks a language, fetch only voices for it (~50 each)
// Avoids pulling 2.4k voices on every page mount.
const VoiceLanguagePicker: React.FC<{
  selectedVoiceId: string;
  onSelectVoice: (id: string) => void;
}> = ({ selectedVoiceId, onSelectVoice }) => {
  const { t } = useTranslation();
  const [languages, setLanguages] = useState<{ language: string; count: number; meta: { flag: string; label: string }; key: string }[]>([]);
  const [activeLanguage, setActiveLanguage] = useState<string>(() => {
    try { return localStorage.getItem('hg_voice_lang') || ''; } catch { return ''; }
  });
  const [voices, setVoices] = useState<HeygenVoice[]>([]);
  const [loadingLangs, setLoadingLangs] = useState(true);
  const [loadingVoices, setLoadingVoices] = useState(false);

  // Phase 1 — language list
  useEffect(() => {
    setLoadingLangs(true);
    videoStudioService.listVoiceLanguages()
      .then(r => {
        if (!r.success) return;
        const enriched = (r.data || []).map(({ language, count }) => {
          const raw = (language || '').toLowerCase();
          const meta = LANG_META[raw] || { flag: '🌐', label: language || 'Outros' };
          const key = LANG_META[raw] ? raw : `other:${language}`;
          return { language, count, meta, key };
        });
        // Sort: PT first, then by count
        enriched.sort((a, b) => {
          if (a.key === 'portuguese') return -1;
          if (b.key === 'portuguese') return 1;
          return b.count - a.count;
        });
        setLanguages(enriched);
        // Pick a sensible default if nothing persisted or persisted is no longer valid
        if (enriched.length && !enriched.some(e => e.language === activeLanguage)) {
          setActiveLanguage(enriched[0].language);
        }
      })
      .finally(() => setLoadingLangs(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phase 2 — voices for the active language. Re-runs whenever language changes.
  useEffect(() => {
    if (!activeLanguage) return;
    setLoadingVoices(true);
    videoStudioService.listVoices({ language: activeLanguage, limit: 500 })
      .then(r => {
        if (r.success) setVoices(r.data || []);
      })
      .finally(() => setLoadingVoices(false));
  }, [activeLanguage]);

  const setActiveLanguagePersisted = (lang: string) => {
    setActiveLanguage(lang);
    try { localStorage.setItem('hg_voice_lang', lang); } catch {}
  };

  const activeMeta = languages.find(l => l.language === activeLanguage)?.meta;
  const selectedVoice = voices.find(v => v.id === selectedVoiceId);

  if (loadingLangs) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500 p-3 rounded-lg bg-gray-100 dark:bg-gray-800">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t('videoStudio.voice.loadingLanguages')}
      </div>
    );
  }
  if (languages.length === 0) {
    return <div className="text-xs text-gray-500 p-3 rounded-lg bg-gray-100 dark:bg-gray-800 text-center">{t('videoStudio.voice.none')}</div>;
  }

  return (
    <div className="space-y-2">
      {/* Phase 1: language select with flag + count */}
      <select
        value={activeLanguage}
        onChange={(e) => setActiveLanguagePersisted(e.target.value)}
        className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary-500/50"
      >
        {languages.map(l => (
          <option key={l.key} value={l.language}>
            {t('videoStudio.voice.languageWithCount', { flag: l.meta.flag, label: l.meta.label, count: l.count })}
          </option>
        ))}
      </select>

      {/* Phase 2: voices for the selected language (loaded on demand) */}
      <div className="flex items-center gap-2">
        <select
          value={selectedVoiceId}
          onChange={(e) => onSelectVoice(e.target.value)}
          disabled={loadingVoices}
          className="flex-1 px-3 py-2 rounded-lg bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary-500/50 disabled:opacity-50"
        >
          {loadingVoices ? (
            <option value="">{t('videoStudio.voice.loadingVoices')}</option>
          ) : !voices.find(v => v.id === selectedVoiceId) ? (
            <option value="">{t('videoStudio.voice.choose', { flag: activeMeta?.flag || '' })}</option>
          ) : null}
          {!loadingVoices && voices.map(v => (
            <option key={v.id} value={v.id}>
              {v.name} {v.gender ? `· ${v.gender}` : ''}
            </option>
          ))}
        </select>
        <VoiceSampleButton previewUrl={selectedVoice?.preview_audio} />
      </div>
      <div className="text-[10px] text-gray-500">
        {loadingVoices
          ? t('videoStudio.voice.fetching')
          : t('videoStudio.voice.countLabel', { count: voices.length, language: activeMeta?.label || activeLanguage })}
      </div>
    </div>
  );
};

// Plays the voice's stock sample audio (provided by HeyGen on the voice list response).
// Free — no credits charged. Falls back to a hint if the voice doesn't expose a sample URL.
const VoiceSampleButton: React.FC<{ previewUrl?: string }> = ({ previewUrl }) => {
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // When the voice changes, stop any currently-playing sample
  useEffect(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    setPlaying(false);
  }, [previewUrl]);

  const toggle = () => {
    if (!previewUrl) {
      toast(t('videoStudio.voice.noSample') as string, { icon: 'ℹ️' });
      return;
    }
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); }
    else { audioRef.current.play().catch(() => toast.error(t('videoStudio.voice.sampleFailed') as string)); }
  };

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        title={previewUrl
          ? (playing ? t('videoStudio.voice.pauseSample') as string : t('videoStudio.voice.playSample') as string)
          : t('videoStudio.voice.noSampleAvailable') as string}
        className={`shrink-0 w-10 h-10 rounded-lg inline-flex items-center justify-center border transition ${
          previewUrl
            ? 'bg-violet-500/15 border-violet-500/40 text-violet-700 dark:text-violet-300 hover:bg-violet-500/25'
            : 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-400 cursor-not-allowed'
        }`}
      >
        {playing ? <Pause className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      </button>
      {previewUrl && (
        <audio
          ref={audioRef}
          src={previewUrl}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          preload="none"
        />
      )}
    </>
  );
};

export default HeygenGenerator;
